import { getGlobalErrorHandlingDecision } from './globalErrorFilter';

declare global {
  interface Window {
    __cm_error_handlers?: boolean;
  }
}

/**
 * Global browser error handlers.
 *
 * Catches Convermax-owned uncaught synchronous exceptions and unhandled
 * promise rejections and forwards them to Loki via the diagnostics module.
 *
 * Imported as a prelude in main/startup.ts — must be loaded before other
 * app code so it catches errors during initialization.
 *
 * Uses dynamic import() for diagnostics so this module has zero static
 * dependencies on the widget/config initialization chain. A static import
 * of diagnostics.js would transitively pull in componentlessWidgetsConfig →
 * widgetProcessor, mutating the DOM before InitFunc runs.
 *
 * - Does NOT call event.preventDefault() so errors still appear in the
 *   browser console for developers.
 * - Idempotent: uses a window flag to guard against double-registration.
 */

/**
 * "Failed to fetch" / "NetworkError when attempting to fetch resource" / "Load failed"
 * are browser-native messages for a fetch() that could not establish a connection.
 * These are already caught and logged as WARN by our server API layer (logNetworkRequest).
 * Any that bubble up here are external network conditions, not code bugs.
 */
const NETWORK_FETCH_ERROR_RE = /failed to fetch|network(?:error| error| request failed)|load failed/i;

function isNetworkFetchError(error: unknown): error is TypeError {
  return error instanceof TypeError && NETWORK_FETCH_ERROR_RE.test(error.message);
}

/**
 * AbortError is thrown when a fetch() is cancelled via AbortController.abort().
 * We intentionally abort requests when a new one supersedes an old one (e.g. rapid
 * YMM selection or search input changes). The serverApi layer already skips logging
 * these entirely; suppress them here too so they never reach logRuntimeError.
 */
function isAbortError(error: unknown): error is Error & { name: 'AbortError' } {
  return error instanceof Error && error.name === 'AbortError';
}

if (!window.__cm_error_handlers) {
  // eslint-disable-next-line camelcase
  window.__cm_error_handlers = true;

  window.addEventListener('error', (event) => {
    try {
      // Ignore events that do not provide useful data, plus any event that
      // cannot be positively tied to our own bundle.
      if (!event.error && !event.message) {
        return;
      }

      // Cross-origin scripts served without CORS headers produce a sanitized
      // "Script error." with no filename, lineno, or stack — completely useless
      // for diagnosis. Drop them to avoid flooding Loki with noise.
      // To get full details from cross-origin bundles, the <script> tag must
      // have crossorigin="anonymous" AND the CDN must send Access-Control-Allow-Origin.
      if (event.message === 'Script error.' && !event.error && !event.filename) {
        return;
      }

      const rawError: unknown = event.error;
      const decision = getGlobalErrorHandlingDecision({ filename: event.filename, error: rawError });
      if (!decision.shouldHandle) {
        return;
      }

      const error: unknown = rawError || new Error(event.message || 'Unknown error');
      const filename = event.filename || '';

      const extra = {
        'error.filename': filename,
        'error.lineno': String(event.lineno ?? ''),
        'error.colno': String(event.colno ?? ''),
        'error.source_match': decision.sourceEvidence?.matchField ?? '',
        'error.source_url': decision.sourceEvidence?.matchedValue ?? '',
      };
      import('Diagnostics/diagnostics')
        .then(({ logRuntimeError }) => logRuntimeError({ source: 'global_error', error, extra }))
        .catch(() => {});
    } catch {
      // diagnostics must never break the app
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const { reason } = event;
      const decision = getGlobalErrorHandlingDecision({ error: reason });
      if (!decision.shouldHandle) {
        return;
      }

      const error = reason instanceof Error ? reason : new Error(String(reason ?? 'Unknown rejection'));

      if (isAbortError(reason)) {
        return; // intentional fetch cancellation — not an error
      }

      if (isNetworkFetchError(reason)) {
        // Network errors from our API calls are already logged as WARN by the
        // server API layer. Route any that bubble up here through
        // logNetworkRequest (WARN) instead of logRuntimeError (ERROR).
        import('Diagnostics/diagnostics')
          .then(({ logNetworkRequest }) =>
            logNetworkRequest({
              endpoint: 'unknown',
              scenario: 'request_error',
              primaryError: { message: error.message, originalError: error },
            }),
          )
          .catch(() => {});
        return;
      }

      const extra = {
        'error.source_match': decision.sourceEvidence?.matchField ?? '',
        'error.source_url': decision.sourceEvidence?.matchedValue ?? '',
      };

      import('Diagnostics/diagnostics')
        .then(({ logRuntimeError }) => logRuntimeError({ source: 'unhandled_rejection', error, extra }))
        .catch(() => {});
    } catch {
      // diagnostics must never break the app
    }
  });
}
